(function (root, factory) {
  if (typeof module === 'object' && module.exports) {
    module.exports = factory(root);
    return;
  }
  if (typeof define === 'function' && define.amd) {
    define([], function () {
      return factory(root);
    });
    return;
  }
  root.t = factory(root);
})(typeof globalThis !== 'undefined' ? globalThis : typeof self !== 'undefined' ? self : typeof window !== 'undefined' ? window : this, function (root) {
  const window = root;
  const config = window.flex4bTranslationsConfig || {};
  const translator = window.t && typeof window.t === 'object' ? window.t : {};
  const storageKey = typeof config.storageKey === 'string' && config.storageKey !== '' ? config.storageKey : 'flex4b.translations';
  const storageVersionKey = storageKey + '.version';
  const debugStorageKey = storageKey + '.debug';
  const sourceFile = typeof config.sourceFile === 'string' ? config.sourceFile : '';
  const version = String(config.version ?? '');
  const storage = (() => {
    try {
      return window.localStorage;
    } catch (error) {
      return null;
    }
  })();
  const debugEnabled = (() => {
    if (config.debug === true || window.flex4bTranslationsDebug === true) {
      return true;
    }
    if (!storage) {
      return false;
    }
    try {
      return storage.getItem(debugStorageKey) === '1';
    } catch (error) {
      return false;
    }
  })();
  const debugLog = (...args) => {
    if (!debugEnabled || !window.console || typeof window.console.log !== 'function') {
      return;
    }
    window.console.log('[flex4b translations]', ...args);
  };
  debugLog('init', {
    sourceFile: sourceFile,
    version: version,
    storageKey: storageKey
  });
  const parseCatalog = rawCatalog => {
    if (typeof rawCatalog !== 'string' || rawCatalog.trim() === '') {
      return null;
    }
    try {
      return JSON.parse(rawCatalog);
    } catch (error) {
      return null;
    }
  };
  const readCachedCatalog = (allowStale = false) => {
    if (!storage) {
      return '';
    }
    try {
      const cachedVersion = storage.getItem(storageVersionKey);
      const cachedCatalog = storage.getItem(storageKey) || '';
      if (cachedCatalog === '') {
        return '';
      }
      if (allowStale || cachedVersion === version) {
        return cachedCatalog;
      }
    } catch (error) {
      return '';
    }
    return '';
  };
  const readValidCachedCatalog = (allowStale = false) => {
    const rawCatalog = readCachedCatalog(allowStale);
    const catalog = parseCatalog(rawCatalog);
    if (!catalog) {
      debugLog('cached catalog invalid or empty', {
        allowStale: allowStale,
        storageKey: storageKey,
        version: version
      });
      return null;
    }
    debugLog('cached catalog loaded', {
      allowStale: allowStale,
      storageKey: storageKey,
      version: version
    });
    return {
      rawCatalog: rawCatalog,
      catalog: catalog
    };
  };
  const writeCachedCatalog = rawCatalog => {
    if (!storage || rawCatalog === '') {
      return;
    }
    try {
      storage.setItem(storageKey, rawCatalog);
      storage.setItem(storageVersionKey, version);
      debugLog('catalog cached', {
        storageKey: storageKey,
        version: version,
        bytes: rawCatalog.length
      });
    } catch (error) {
      // Ignore quota and storage access errors.
      debugLog('catalog cache write failed', {
        storageKey: storageKey,
        version: version
      }, error);
    }
  };
  const fetchCatalogSync = () => {
    if (sourceFile === '') {
      debugLog('fetch skipped, missing source file');
      return '';
    }
    const request = new XMLHttpRequest();
    const separator = sourceFile.indexOf('?') === -1 ? '?' : '&';
    const requestUrl = sourceFile + separator + 'v=' + encodeURIComponent(version);
    debugLog('fetch start', {
      requestUrl: requestUrl,
      sourceFile: sourceFile,
      version: version
    });
    try {
      request.open('GET', requestUrl, false);
      request.send(null);
    } catch (error) {
      debugLog('fetch failed', {
        requestUrl: requestUrl,
        sourceFile: sourceFile,
        version: version
      }, error);
      return '';
    }
    if (request.status >= 200 && request.status < 300) {
      debugLog('fetch success', {
        requestUrl: requestUrl,
        status: request.status,
        bytes: (request.responseText || '').length
      });
      return request.responseText || '';
    }
    debugLog('fetch returned non-success status', {
      requestUrl: requestUrl,
      status: request.status
    });
    return '';
  };
  const cachedCatalog = readValidCachedCatalog();
  const staleCatalog = cachedCatalog === null ? readValidCachedCatalog(true) : null;
  let catalog = cachedCatalog ? cachedCatalog.catalog : null;
  if (!catalog) {
    const rawFetchedCatalog = fetchCatalogSync();
    const fetchedCatalog = parseCatalog(rawFetchedCatalog);
    if (fetchedCatalog) {
      debugLog('fetched catalog parsed successfully', {
        storageKey: storageKey,
        version: version
      });
      writeCachedCatalog(rawFetchedCatalog);
      catalog = fetchedCatalog;
    } else if (staleCatalog) {
      debugLog('fetched catalog invalid, falling back to stale cache', {
        storageKey: storageKey,
        version: version
      });
      catalog = staleCatalog.catalog;
    } else {
      debugLog('no valid catalog available after fetch', {
        storageKey: storageKey,
        version: version
      });
    }
  }
  catalog = catalog || {
    messages: {}
  };
  const messages = catalog && typeof catalog.messages === 'object' && catalog.messages !== null ? catalog.messages : {};
  const getMessageEntry = (context, message) => {
    const contextKey = context || '';
    if (messages[contextKey] && typeof messages[contextKey] === 'object' && Object.prototype.hasOwnProperty.call(messages[contextKey], message)) {
      return messages[contextKey][message];
    }
    return undefined;
  };
  const normalizeTranslation = (entry, fallback) => {
    if (Array.isArray(entry)) {
      for (const item of entry) {
        if (typeof item === 'string' && item !== '') {
          return item;
        }
      }
    }
    if (typeof entry === 'string' && entry !== '') {
      return entry;
    }
    return fallback;
  };
  const applyNamedParameters = (text, values) => {
    if (!values || typeof values !== 'object' || Array.isArray(values)) {
      return text;
    }
    return text.replace(/\{([A-Za-z0-9_]+)\}/g, (match, key, offset, fullText) => {
      const previousCharacter = offset > 0 ? fullText[offset - 1] : '';
      const nextCharacter = fullText[offset + match.length] || '';
      if (previousCharacter === '{' || nextCharacter === '}') {
        return match;
      }
      if (Object.prototype.hasOwnProperty.call(values, key)) {
        return String(values[key]);
      }
      return match;
    });
  };
  const applyPrintfParameters = (text, values) => {
    if (!Array.isArray(values) || values.length === 0) {
      return text;
    }
    let valueIndex = 0;
    return text.replace(/%[sd]/g, match => {
      if (valueIndex >= values.length) {
        return match;
      }
      const value = values[valueIndex];
      valueIndex += 1;
      return String(value);
    });
  };
  const translate = (context, message, args) => {
    const translation = normalizeTranslation(getMessageEntry(context, message), message);
    if (args.length === 1 && args[0] && typeof args[0] === 'object' && !Array.isArray(args[0])) {
      return applyNamedParameters(translation, args[0]);
    }
    if (args.length > 0) {
      return applyPrintfParameters(translation, args);
    }
    return translation;
  };
  translator.catalog = catalog;
  translator.messages = messages;
  translator.__ = function (message, ...args) {
    return translate('', message, args);
  };
  translator.p__ = function (context, message, ...args) {
    return translate(context, message, args);
  };
  translator.gettext = translator.__;
  translator.pgettext = translator.p__;
  translator.ngettext = function (singular, plural, count, ...args) {
    const entry = getMessageEntry('', singular);
    let translation;
    if (Array.isArray(entry)) {
      translation = count === 1 ? normalizeTranslation(entry[0], singular) : normalizeTranslation(entry[1], plural);
    } else {
      translation = count === 1 ? normalizeTranslation(entry, singular) : plural;
    }
    return applyPrintfParameters(translation, [count, ...args]);
  };
  return translator;
});